import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

import { AuthLayout } from "@/components/AuthLayout";
import { ApiError, useAuth } from "@/lib/auth";

/**
 * Login page rendered through the v2 AuthLayout shell. Preserves all auth
 * behaviour from the Material card original: emailRef autofocus, ApiError
 * branches (401, 429, USER_INACTIVE, generic), submitting state, password
 * show/hide via Eye / EyeOff, the data-testid="login-error" hook, and the
 * `from`-redirect preservation when ProtectedRoute bounced the user here.
 */
export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  // Early-return AFTER all hooks have run so the hook order stays stable
  // across the pre-auth and post-auth render (Rules of Hooks).
  const from =
    (location.state as { from?: { pathname?: string } } | null)?.from
      ?.pathname ?? "/dashboard";
  if (user != null) {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      const fromPath = (location.state as { from?: { pathname?: string } } | null)
        ?.from?.pathname;
      navigate(fromPath ?? "/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          const retry = (err.body.retryAfterSec as number | undefined) ?? 0;
          setError(
            retry > 0
              ? `Too many attempts. Try again in ${retry} seconds.`
              : "Too many attempts. Try again later.",
          );
        } else if (err.code === "USER_INACTIVE") {
          setError("This account has been deactivated. Contact an admin.");
        } else if (err.status === 401) {
          setError("Email or password is incorrect");
        } else {
          setError(err.message || "Unexpected error");
        }
      } else {
        setError("Network error. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout title="Welcome back." subtitle="Sign in to your workspace.">
      <form onSubmit={handleSubmit} noValidate className="auth-form-body">
        <div className="auth-field">
          <label className="label" htmlFor="login-email">Email</label>
          <input
            id="login-email"
            ref={emailRef}
            className="input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="auth-field">
          <label className="label" htmlFor="login-password">Password</label>
          <div className="auth-input-wrap">
            <input
              id="login-password"
              className="input"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="auth-eye"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
            >
              {showPassword ? (
                <EyeOff size={16} strokeWidth={1.6} aria-hidden />
              ) : (
                <Eye size={16} strokeWidth={1.6} aria-hidden />
              )}
            </button>
          </div>
          <div className="auth-field-help">
            <Link to="/forgot-password">Forgot password?</Link>
          </div>
        </div>

        {error && (
          <p role="alert" data-testid="login-error" className="auth-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="btn btn-pop auth-submit"
          disabled={submitting || !email || !password}
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>

        <p className="auth-alt">
          No workspace yet? <Link to="/register">Create one</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
